import { TextFieldModule } from "@angular/cdk/text-field";
import { AsyncPipe, CommonModule } from "@angular/common";
import { ChangeDetectorRef, Component, Inject, OnInit } from "@angular/core";
import { AbstractControl, FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, ValidationErrors, ValidatorFn, Validators } from "@angular/forms";
import { MatAutocompleteModule } from "@angular/material/autocomplete";
import { MatButtonModule } from "@angular/material/button";
import { MatCheckboxModule } from "@angular/material/checkbox";
import { MatOptionModule, MatRippleModule } from "@angular/material/core";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatIconModule } from "@angular/material/icon";
import { MatInputModule } from "@angular/material/input";
import { MatSelectChange, MatSelectModule } from "@angular/material/select";
import { MatTooltipModule } from "@angular/material/tooltip";
import { RouterLink, RouterOutlet } from "@angular/router";
import { QRCodeModule } from "angularx-qrcode";
import { EnumViewModel, PartLocationDto, PartLocationType } from "app/api/models";
import { PartService } from "app/api/services";
import { sortOnSelf } from "app/app.component";
import { SharedModule } from "app/core/shared.module";
import { isNumber } from "lodash";


@Component({
    selector       : 'part-location-dialog',
    templateUrl    : './part-location-dialog.component.html',
    standalone: true,
    imports: [
        CommonModule,
        SharedModule,
        RouterOutlet,
        MatFormFieldModule,
        MatIconModule,
        MatInputModule,
        FormsModule,
        ReactiveFormsModule,
        MatButtonModule,
        RouterLink,
        AsyncPipe,
        MatTooltipModule,
        MatRippleModule,
        MatCheckboxModule,
        MatSelectModule,
        MatOptionModule,
        TextFieldModule,
        MatAutocompleteModule,
        QRCodeModule,
    ]
})
export class PartLocationDialogComponent implements OnInit
{
    public form: FormGroup;
    public location: PartLocationDto;

    private allLocations: PartLocationDto[] = [];

    public locationTypes = sortOnSelf(Object.values(PartLocationType).filter(x => isNumber(x)), false).map(x => <EnumViewModel> {name: this.getLocationTypeName(+x), value: <PartLocationType>x});
    public parentLocations: PartLocationDto[] = [];

    public PartLocationType = PartLocationType;

    constructor(
        public dialogRef: MatDialogRef<PartLocationDialogComponent>,
        private partService: PartService,
        private _formBuilder: FormBuilder,
        private cdr: ChangeDetectorRef,
        @Inject(MAT_DIALOG_DATA) public data: {location: PartLocationDto}
    )
    {}

    ngOnInit(): void
    {
        this.location = this.data.location;

        this.loadLocations();
        this.buildForm();
    }

    loadLocations() {
        this.partService.apiPartLocationsGet$Json$Response().subscribe(res => {
            if(!res?.body?.data) return;
            this.allLocations = res.body.data;

            if(this.location)
                this.parentLocations = this.allLocations.filter(x => x.locationType == this.getParentLocationType(this.location.locationType));
        });
    }

    buildForm() {
        this.form = this._formBuilder.group({
            code                    : [this.location?.code              ?? '',      Validators.required],
            name                    : [this.location?.name              ?? '',      Validators.required],
            locationType            : [this.location?.locationType      ?? null,    Validators.required],
            barcode                 : [this.location?.barcode           ?? null],
            capacity                : [this.location?.capacity          ?? null],
            notes                   : [this.location?.notes             ?? ''],
            parentLocationId        : [this.location?.parentLocationId  ?? null,    this.requiredIfNotZoneTypeValidator()],
        });

        this.cdr.detectChanges();
    }

    requiredIfNotZoneTypeValidator(): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            const parent = control.parent;
            if (!parent) return null;

            const locationType = parent.get('locationType')?.value;
            const isExempt = locationType == PartLocationType.Warehouse;

            return !isExempt && !control.value ? { required: true } : null;
        };
    }

    locationTypeChanged(event: MatSelectChange) {
        var value = event.value as PartLocationType;

        if(value == PartLocationType.Warehouse)
            this.parentLocations = [];
        else
            this.parentLocations = this.allLocations.filter(x => x.locationType == this.getParentLocationType(value));
    }

    requiresParentId() {
        return this.form?.get('locationType')?.value != PartLocationType.Warehouse;
    }

    getParentLocationType(locationType: PartLocationType) {
        switch(locationType) {
            case PartLocationType.Bin: return PartLocationType.Shelf;
            case PartLocationType.Shelf: return PartLocationType.Rack;
            case PartLocationType.Rack: return PartLocationType.Zone;
            case PartLocationType.Zone: return PartLocationType.Warehouse;
            case PartLocationType.Warehouse: return null;
        }
    }

    getLocationTypeName(locationType: PartLocationType) {
        switch(locationType) {
            case PartLocationType.Bin: return 'Bin (Within Shelf)';
            case PartLocationType.Shelf: return 'Shelf (Within Rack)';
            case PartLocationType.Rack: return 'Rack (Within Zone)';
            case PartLocationType.Zone: return 'Zone (Within Warehouse)';
            case PartLocationType.Warehouse: return 'Warehouse (Top Level)';
            default: return PartLocationType[locationType];
        }
    }
    
    getFullLocationPath(): string {
        var code = this.form.controls['code'].value;
        if (!code) return '';

        var parentId = this.form.controls['parentLocationId'].value;
        var parts = [code];

        while (parentId) {
            var parent = this.allLocations.find(x => x.partLocationId == parentId);
            if (!parent) break;
            parts.unshift(parent.code);
            parentId = parent.parentLocationId;
        }

        return parts.join('-');
    }

    printQrCode() {
        var printArea = document.getElementById('qr-print-area');
        var code = this.getFullLocationPath();
        if(!printArea || !code) return;

        var qrSvg = printArea.querySelector('qrcode')?.innerHTML ?? '';

        var printWindow = window.open('', '_blank', 'width=300,height=200');
        printWindow.document.write(`
            <html><head><title>Label - ${code}</title>
            <style>
                @page { size: 37mm 25mm; margin: 0; }
                body { display: flex; flex-direction: column; align-items: center; justify-content: center; width: 37mm; height: 25mm; margin: 0; padding: 1mm; box-sizing: border-box; font-family: Arial, sans-serif; }
                .qr-container { text-align: center; }
                .qr-container svg { width: 18mm; height: 18mm; }
                .code-label { font-weight: bold; font-size: 6pt; margin-top: 0.5mm; }
            </style></head><body>
            <div class="qr-container">${qrSvg}</div>
            <div class="code-label">${code}</div>
            </body></html>
        `);
        printWindow.document.close();
        printWindow.onload = () => { printWindow.print(); printWindow.close(); };
    }

    save() {
        this.dialogRef.close(this.form.value);
    }

    close() {
        this.dialogRef.close(null);
    }
}